import { RENDER_DELAY_MS } from "@orbio/shared/constants";
import type { CellSnap, FoodSnap, LeaderEntry } from "@orbio/shared/protocol";

export interface BufSnap {
  tick: number;
  recvAt: number;
  cells: CellSnap[];
  food: FoodSnap[];
  leaderboard: LeaderEntry[];
}

export interface ViewSnap {
  cells: CellSnap[];
  food: FoodSnap[];
  leaderboard: LeaderEntry[];
}

export class SnapshotBuffer {
  private buf: BufSnap[] = [];

  push(s: BufSnap): void {
    this.buf.push(s);
    while (this.buf.length > 30) this.buf.shift();
  }

  /** Returns the interpolated view at the given clock time. */
  interpolate(nowMs: number): ViewSnap {
    if (this.buf.length === 0) return { cells: [], food: [], leaderboard: [] };
    const target = nowMs - RENDER_DELAY_MS;
    // Find the two snapshots that bracket `target`.
    let prev = this.buf[0]!;
    let next = this.buf[this.buf.length - 1]!;
    for (let i = 0; i < this.buf.length - 1; i++) {
      if (this.buf[i]!.recvAt <= target && this.buf[i + 1]!.recvAt >= target) {
        prev = this.buf[i]!;
        next = this.buf[i + 1]!;
        break;
      }
    }
    if (prev === next) return { cells: prev.cells, food: prev.food, leaderboard: prev.leaderboard };
    const span = next.recvAt - prev.recvAt;
    const t = span > 0 ? Math.max(0, Math.min(1, (target - prev.recvAt) / span)) : 0;
    // Iterate next.cells so newly-spawned cells (e.g. a freshly-split
    // twin) render on the same frame they arrive. Interpolate from prev
    // when the cell existed there; otherwise just take next as-is.
    const cells: CellSnap[] = next.cells.map((n) => {
      const p = prev.cells.find(c => c.id === n.id);
      if (!p) return n;
      return {
        ...n,
        x: p.x + (n.x - p.x) * t,
        y: p.y + (n.y - p.y) * t,
        r: p.r + (n.r - p.r) * t,
        m: p.m + (n.m - p.m) * t,
      };
    });
    // Interpolate food positions too — they move when a vacuum buff is active.
    const food: FoodSnap[] = next.food.map((n) => {
      const p = prev.food.find(f => f.id === n.id);
      if (!p) return n;
      return { ...n, x: p.x + (n.x - p.x) * t, y: p.y + (n.y - p.y) * t };
    });
    return { cells, food, leaderboard: next.leaderboard };
  }
}
